<template>
  <FormContainer
    v-if="moduleEnCours?.typeModule === 'Externe'"
    withoutDivider
  >
    <template #title>Établissement</template>
    <template #content>
      <div class="form__bloc">
        <WrapperAutocomplete
          id="field_unknown"
          key-id="siret.valeur"
          key-label="denomination"
          :model-value="moduleEnCours?.etablissement"
          @update:model-value="(value) => updateEtablissement(value!)"
          label="Rechercher un organisme de formation externe"
          class="col-18 col-md-22 col-sm-36"
          :search-min-length="9"
          :hint="HINT_SIREN_SIRET_OF"
          :placeholder="PLACE_HOLDER_SIREN"
          strict
          :required="metaModuleEnCours?.etablissement?.$status === PropertyStatus.Required"
          :alertLabel="metaModuleEnCours?.etablissement?.$messageErreurDirty"
          :readonly
          :disabled="moduleEnCours?.isPreSaisieOf === true"
          :search="searchPrestataire"
          :search-formater="sirenSiretSearchFormater"
        >
          <template v-slot="{ denomination, siret, adresse, estEtablissementSiege, etatAdministratif }">
            <div>
              <div>
                <BasicText weight="bold">{{ denomination }} - {{ siret.valeur }}</BasicText>
                <BasicText
                  v-if="estEtablissementSiege === true"
                  color="primary-600"
                >
                  - SIEGE
                </BasicText>
                <BasicText
                  v-if="etatAdministratif === 'F'"
                  color="red"
                >
                  (Inactif)
                </BasicText>
              </div>
              <div style="margin-top: -3px">
                <BasicText size="body-m">
                  {{ adresse?.voie }} {{ adresse?.codePostal }} {{ adresse?.commune }}
                </BasicText>
              </div>
            </div>
          </template>
        </WrapperAutocomplete>
      </div>

      <div class="form__bloc">
        <WrapperInput
          id="field_18"
          :model-value="moduleEnCours?.etablissement?.siret?.valeur"
          @update:model-value="(value) => updateModule('etablissement.siret', activeModuleIndex!, value)"
          label="SIRET"
          class="col-8 col-md-12 col-sm-36"
          :required="metaModuleEnCours?.etablissement?.siret?.valeur?.$status === PropertyStatus.Required"
          :alertLabel="metaModuleEnCours?.etablissement?.siret?.valeur?.$messageErreurDirty!"
          disabled
        />

        <WrapperInput
          id="field_19"
          :model-value="moduleEnCours?.etablissement?.nda"
          @update:model-value="(value) => updateModule('etablissement.nda', activeModuleIndex!, value)"
          label="N° d'activité"
          class="col-8 col-md-8 col-sm-36"
          :required="metaModuleEnCours?.etablissement?.nda?.$status === PropertyStatus.Required"
          :alertLabel="metaModuleEnCours?.etablissement?.nda?.$messageErreurDirty!"
          disabled
        />
        <WrapperInput
          id="field_16"
          :model-value="qualiopiLibelle"
          @update:model-value="
            (value) => updateModule('etablissement.certificationQualiopi', activeModuleIndex!, value)
          "
          label="Qualiopi"
          class="col-20 col-md-16 col-sm-36"
          disabled
          help="La certification Qualiopi est obligatoire pour tous les prestataires d'actions concourant au développement des compétences, et ce depuis le 1er janvier 2022."
        />
      </div>
      <div class="form__bloc">
        <WrapperInput
          id="field_20"
          :model-value="moduleEnCours?.etablissement?.adresse?.voie"
          @update:model-value="(value) => updateModule('etablissement.adresse.voie', activeModuleIndex!, value)"
          :label="LABEL_NUMERO_VOIE"
          class="col-12 col-md-16 col-sm-36"
          :required="metaModuleEnCours?.etablissement?.adresse?.voie?.$status === PropertyStatus.Required"
          :alertLabel="metaModuleEnCours?.etablissement?.adresse?.voie?.$messageErreurDirty!"
          disabled
        />
        <WrapperInput
          id="field_21"
          :model-value="moduleEnCours?.etablissement?.adresse?.complement"
          @update:model-value="(value) => updateModule('etablissement.adresse.complement', activeModuleIndex!, value)"
          label="Complément d'adresse"
          class="col-12 col-md-16 col-sm-36"
          :required="metaModuleEnCours?.etablissement?.adresse?.complement?.$status === PropertyStatus.Required"
          :alertLabel="metaModuleEnCours?.etablissement?.adresse?.complement?.$messageErreurDirty!"
          disabled
        />
        <WrapperInput
          id="field_22"
          :model-value="moduleEnCours?.etablissement?.adresse?.codePostal"
          @update:model-value="(value) => updateModule('etablissement.adresse.codePostal', activeModuleIndex!, value)"
          label="Code postal"
          class="col-4 col-md-6 col-sm-36"
          :required="metaModuleEnCours?.etablissement?.adresse?.codePostal?.$status === PropertyStatus.Required"
          :alertLabel="metaModuleEnCours?.etablissement?.adresse?.codePostal?.$messageErreurDirty!"
          disabled
        />
        <WrapperInput
          id="field_23"
          :model-value="moduleEnCours?.etablissement?.adresse?.commune"
          @update:model-value="(value) => updateModule('etablissement.adresse.commune', activeModuleIndex!, value)"
          label="Commune"
          class="col-8 col-md-10 col-sm-36"
          :required="metaModuleEnCours?.etablissement?.adresse?.commune?.$status === PropertyStatus.Required"
          :alertLabel="metaModuleEnCours?.etablissement?.adresse?.commune?.$messageErreurDirty!"
          disabled
        />
      </div>
    </template>
  </FormContainer>
</template>

<script setup lang="ts">
  import { computed, inject } from 'vue'
  import { storeToRefs } from 'pinia'
  import type { ModuleDTO } from '@/features/api/extranet/dto/Demande/AF'
  import { PropertyStatus } from '@/features/domaine/property-status/PropertyStatus'
  import { CertificationQualiopiLibelle } from '@/features/domaine/types/opco'
  import { useDemandeAFStore } from '@/features/interface/demande/af/useDemandeAFStore'
  import {
    FormContainer,
    HINT_SIREN_SIRET_OF,
    LABEL_NUMERO_VOIE,
    PLACE_HOLDER_SIREN,
  } from '@/features/interface/demande/shared'
  import { AF_ACTIONS } from '@/features/interface/demande/af/useDemandeAFActions.ts'
  import { sirenSiretSearchFormater } from '@/features/interface/shared/tools'
  import { AF_MODULES_ACTIONS } from '../../useModuleActions'

  const store = useDemandeAFStore()
  const { setupArrayUpdaterWithKey } = store

  const { searchPrestataire } = inject(AF_ACTIONS)!

  const { moduleEnCours, activeModuleIndex, metaModuleEnCours, updateEtablissement } = inject(AF_MODULES_ACTIONS)!

  const { readonly, model } = storeToRefs(store)

  const updateModule = setupArrayUpdaterWithKey<ModuleDTO>('formation.modules')

  const qualiopiLibelle = computed(() => {
    return (
      model.value?.formation?.modules?.[activeModuleIndex.value!]?.etablissement?.certificationQualiopi
        ?.map((item) => CertificationQualiopiLibelle[item])
        ?.join(', ') ?? ''
    )
  })
</script>
